/**
 * "Major problems" and "Major changes" monitors (quick-261104-ia3).
 * Severity carries an icon + a word, never colour alone.
 */
import { dayLabel } from '../../lib/exec/fmt.js';
import { sfLink } from '../../lib/exec/links.js';

import { DashTile, TileEmpty } from './DashTile.js';

import type { ChangeItem, ProblemItem, Severity } from '../../lib/exec/monitors.js';

const SEV: Record<Severity, { icon: string; word: string; cls: string }> = {
  critical: { icon: '⛔', word: 'Critical', cls: 'bg-bad-bg text-bad-text' },
  serious: { icon: '▲', word: 'Serious', cls: 'bg-warn-bg text-warn-text' },
  warning: { icon: '●', word: 'Watch', cls: 'bg-ink-100 text-ink-700' },
};

export function ProblemsTile({
  items,
  loading,
  onOpenTab,
  onOpenPotential,
}: {
  items: ProblemItem[];
  loading: boolean;
  onOpenTab: (tab: NonNullable<ProblemItem['tab']>) => void;
  onOpenPotential?: () => void;
}): React.JSX.Element {
  const crit = items.filter((i) => i.severity === 'critical').length;
  return (
    <DashTile
      title="Major problems"
      info="The issues that most need attention right now, worst first. Each is checked automatically against the live numbers on this page; click one to open the tab with the detail."
      headline={loading ? '—' : String(items.length)}
      sub={loading ? '' : crit > 0 ? `${String(crit)} critical` : items.length === 0 ? 'nothing flagged' : 'none critical'}
      size="xl"
      footer="Rules: coverage, churn, renewals, stalled deals, meetings, win rate, data hygiene"
      testId="exec-problems"
    >
      {items.length === 0 ? (
        <TileEmpty text={loading ? 'Checking…' : 'No major problems detected.'} />
      ) : (
        <ul className="h-full space-y-2 overflow-y-auto pr-1">
          {items.map((p) => (
            <li key={p.title}>
              <button
                type="button"
                disabled={!p.tab && !(p.opensPotential && onOpenPotential)}
                onClick={() => {
                  if (p.opensPotential && onOpenPotential) onOpenPotential();
                  else if (p.tab) onOpenTab(p.tab);
                }}
                className="w-full rounded-lg border border-ink-100 p-2 text-left hover:border-brand-300 disabled:cursor-default disabled:hover:border-ink-100"
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.6rem] font-semibold ${SEV[p.severity].cls}`}>
                    <span aria-hidden>{SEV[p.severity].icon}</span>
                    {SEV[p.severity].word}
                  </span>
                  <span className="text-xs font-semibold leading-snug text-ink-900">{p.title}</span>
                </div>
                <p className="mt-1 text-[0.7rem] leading-snug text-ink-500">{p.detail}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </DashTile>
  );
}

const KIND: Record<ChangeItem['kind'], string> = {
  won: '🏆',
  lost: '✕',
  created: '＋',
  advanced: '→',
  slipped: '⏱',
  arr: '$',
  pipeline: '≡',
};

export function ChangesTile({ items, loading }: { items: ChangeItem[]; loading: boolean }): React.JSX.Element {
  const wins = items.filter((i) => i.kind === 'won').length;
  return (
    <DashTile
      title="Major changes"
      info="What moved recently: deals won or lost in the last 30 days, new opportunities and big stage moves in the last 1–2 weeks, deals that pushed their close date, and the month's ARR and pipeline swing. Green is good news, red is bad."
      headline={loading ? '—' : String(items.length)}
      sub={loading ? '' : `${String(wins)} win${wins === 1 ? '' : 's'} in 30 days`}
      size="xl"
      footer="Deals ≥ $25K created · ≥ $50K moved or slipped · all wins and losses"
      testId="exec-changes"
    >
      {items.length === 0 ? (
        <TileEmpty text={loading ? 'Loading…' : 'No major changes in the window.'} />
      ) : (
        <ul className="h-full space-y-1.5 overflow-y-auto pr-1">
          {items.map((c, i) => {
            const tone = c.good === null ? 'text-ink-500' : c.good ? 'text-good-text' : 'text-bad-text';
            const body = (
              <>
                <span aria-hidden className={`w-4 shrink-0 text-center text-xs ${tone}`}>
                  {KIND[c.kind]}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-ink-900">{c.title}</span>
                  <span className="block truncate text-[0.68rem] text-ink-500">{c.detail}</span>
                </span>
                <span className="shrink-0 text-[0.66rem] tabular-nums text-ink-400">{dayLabel(c.date)}</span>
              </>
            );
            return (
              <li key={`${c.kind}-${c.title}-${String(i)}`} className="border-b border-ink-100 pb-1.5 last:border-0">
                {c.href ? (
                  <a href={sfLink(c.href)} className="flex items-start gap-2 hover:opacity-80">
                    {body}
                  </a>
                ) : (
                  <div className="flex items-start gap-2">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </DashTile>
  );
}
